import { useMemo } from 'react';
import { CATEGORIES, COLORS, TYPE_COLORS_BG, TYPE_COLORS_FG, TYPE_LABELS } from '../constants';
import { compareActivityDesc, isThisWeek } from '../itemDates';
import type { Category, ParsedItem } from '../types';
import { ImpactDot } from './ImpactDot';

type Props = {
  items: ParsedItem[];
  onItemClick: (item: ParsedItem) => void;
  // Muted items only reach this component when "Näytä kaikki" is on; they
  // render dimmed.
  isMuted?: (id: string) => boolean;
  actionLimit?: number;
  weekLimit?: number;
  weekWindowDays?: number;
};

const MS_PER_DAY = 86_400_000;

// Hero surface. Two focused widgets side by side, one per user job:
//
// Left (2/3) — "Toimenpiteet": items that need action. Either
//   actionNeeded=true or a parsed future effectiveDate (retirements with
//   a dated deadline, preview/rollout windows). Sorted by urgency.
//
// Right (1/3) — "Viikon kärki": critical/high items first seen or changed in
//   the last 7 days (see isThisWeek). Replaces the old "what should I know
//   about this week" job.
//
// Both surfaces hand click events up to the parent so a single DetailPanel
// renders against the full dataset regardless of which surface fired it.
export function HeroSplit({
  items,
  onItemClick,
  isMuted,
  actionLimit = 6,
  weekLimit = 4,
  weekWindowDays = 7,
}: Props) {
  const now = Date.now();

  const actions = useMemo(() => {
    const ranked = items
      .map((item) => ({ item, urgency: urgencyScore(item, now) }))
      .filter((x) => x.urgency !== null)
      .sort((a, b) => (a.urgency as number) - (b.urgency as number));
    return ranked.slice(0, actionLimit).map((x) => x.item);
  }, [items, now, actionLimit]);

  const week = useMemo(
    () =>
      items
        .filter(
          (i) =>
            (i.impact === 'critical' || i.impact === 'high') &&
            isThisWeek(i, now, weekWindowDays),
        )
        .sort(compareActivityDesc)
        .slice(0, weekLimit),
    [items, now, weekLimit, weekWindowDays],
  );

  if (actions.length === 0 && week.length === 0) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
        gap: 16,
        marginBottom: 22,
      }}
    >
      <ActionColumn items={actions} onItemClick={onItemClick} isMuted={isMuted} now={now} />
      <WeekColumn
        items={week}
        windowDays={weekWindowDays}
        onItemClick={onItemClick}
        isMuted={isMuted}
      />
    </div>
  );
}

function ActionColumn({
  items,
  onItemClick,
  isMuted,
  now,
}: {
  items: ParsedItem[];
  onItemClick: (item: ParsedItem) => void;
  isMuted?: (id: string) => boolean;
  now: number;
}) {
  return (
    <ColumnShell
      title="Toimenpiteet"
      subtitle="Vaatii huomiotasi"
      count={items.length}
      accent="#A80000"
      emptyLabel="Ei avoimia toimenpiteitä — hyvä homma."
      isEmpty={items.length === 0}
    >
      {items.map((item) => (
        <ActionRow
          key={item.id}
          item={item}
          now={now}
          muted={!!isMuted?.(item.id)}
          onClick={() => onItemClick(item)}
        />
      ))}
    </ColumnShell>
  );
}

function WeekColumn({
  items,
  windowDays,
  onItemClick,
  isMuted,
}: {
  items: ParsedItem[];
  windowDays: number;
  onItemClick: (item: ParsedItem) => void;
  isMuted?: (id: string) => boolean;
}) {
  return (
    <ColumnShell
      title={`Viikon kärki`}
      subtitle={`Kriittiset & korkeat ${windowDays} pv:ltä`}
      count={items.length}
      accent="#0078D4"
      emptyLabel="Ei merkittäviä viikon sisällä."
      isEmpty={items.length === 0}
    >
      {items.map((item) => (
        <WeekRow
          key={item.id}
          item={item}
          muted={!!isMuted?.(item.id)}
          onClick={() => onItemClick(item)}
        />
      ))}
    </ColumnShell>
  );
}

function ColumnShell({
  title,
  subtitle,
  count,
  accent,
  emptyLabel,
  isEmpty,
  children,
}: {
  title: string;
  subtitle: string;
  count: number;
  accent: string;
  emptyLabel: string;
  isEmpty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--surface, #F8FAFC)',
        border: '1px solid var(--border, #E5E7EB)',
        borderRadius: 12,
        padding: 18,
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 14,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: 'var(--text, #111827)',
              letterSpacing: -0.2,
            }}
          >
            {title}
          </div>
          <div
            style={{
              background: `${accent}15`,
              color: accent,
              fontSize: 11,
              fontWeight: 800,
              padding: '1px 8px',
              borderRadius: 10,
            }}
          >
            {count}
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted, #6B7280)' }}>{subtitle}</div>
      </div>

      {isEmpty ? (
        <div
          style={{
            padding: '24px 4px',
            textAlign: 'center',
            color: 'var(--text-muted, #9CA3AF)',
            fontSize: 12,
            fontStyle: 'italic',
          }}
        >
          {emptyLabel}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
      )}
    </div>
  );
}

function ActionRow({
  item,
  now,
  muted,
  onClick,
}: {
  item: ParsedItem;
  now: number;
  muted: boolean;
  onClick: () => void;
}) {
  const days = daysUntilEffective(item, now);
  const catLabel =
    CATEGORIES.find((c) => c.value === (item.category as Category))?.label ?? item.category;
  const catColor = COLORS.category[item.category as Category];
  const countdown = daysPill(days);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 8,
        textAlign: 'left',
        background: 'var(--surface-raised, rgba(255,255,255,0.5))',
        border: '1px solid var(--border-soft, rgba(148,163,184,0.2))',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: 'inherit',
        width: '100%',
        opacity: muted ? 0.5 : 1,
        transition: 'background 0.12s, border-color 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--surface-hover, rgba(0,120,212,0.06))';
        e.currentTarget.style.borderColor = 'var(--border, rgba(148,163,184,0.45))';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--surface-raised, rgba(255,255,255,0.5))';
        e.currentTarget.style.borderColor = 'var(--border-soft, rgba(148,163,184,0.2))';
      }}
    >
      <ImpactDot impact={item.impact} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text, #111827)',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
          title={item.title}
        >
          {stripRetirementPrefix(item.title)}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 4,
            fontSize: 10,
            color: 'var(--text-muted, #6B7280)',
          }}
        >
          <span
            style={{
              background: `${catColor}18`,
              color: catColor,
              padding: '1px 7px',
              borderRadius: 8,
              fontWeight: 700,
              letterSpacing: 0.2,
            }}
          >
            {catLabel}
          </span>
          {days !== null && (
            <span>{new Date(item.effectiveDate as string).toLocaleDateString('fi-FI')}</span>
          )}
        </div>
      </div>
      <div
        style={{
          background: `${countdown.color}18`,
          color: countdown.color,
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 800,
          whiteSpace: 'nowrap',
          textAlign: 'center',
          minWidth: 64,
        }}
      >
        {countdown.label}
      </div>
    </button>
  );
}

function WeekRow({
  item,
  muted,
  onClick,
}: {
  item: ParsedItem;
  muted: boolean;
  onClick: () => void;
}) {
  const typeBg = TYPE_COLORS_BG[item.updateType];
  const typeFg = TYPE_COLORS_FG[item.updateType];
  const typeLabel = TYPE_LABELS[item.updateType];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 10px',
        borderRadius: 8,
        textAlign: 'left',
        background: 'var(--surface-raised, rgba(255,255,255,0.5))',
        border: '1px solid var(--border-soft, rgba(148,163,184,0.2))',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: 'inherit',
        opacity: muted ? 0.5 : 1,
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--surface-hover, rgba(0,120,212,0.06))';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--surface-raised, rgba(255,255,255,0.5))';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            background: typeBg,
            color: typeFg,
            fontSize: 9,
            fontWeight: 800,
            padding: '2px 7px',
            borderRadius: 6,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {typeLabel}
        </span>
        <ImpactDot impact={item.impact} />
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text, #111827)',
          lineHeight: 1.35,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
        title={item.title}
      >
        {stripTypePrefix(item.title)}
      </div>
    </button>
  );
}

// Lower urgency score = more urgent (sorted ascending). Retirements with a
// date come first (by days-to-go), then action-needed items without an
// explicit date, then nothing. Returning null excludes the item.
function urgencyScore(item: ParsedItem, now: number): number | null {
  const days = daysUntilEffective(item, now);
  if (item.updateType === 'deprecation' && days !== null) {
    // Datedretirements: rank strictly by days to go.
    return days;
  }
  if (item.actionNeeded) {
    // Action-needed without a date: land after the 180-day window so dated
    // retirements always come first, but still visible.
    return 1000;
  }
  return null;
}

function daysUntilEffective(item: ParsedItem, now: number): number | null {
  if (!item.effectiveDate) return null;
  const t = new Date(item.effectiveDate).getTime();
  if (isNaN(t)) return null;
  const days = Math.round((t - now) / MS_PER_DAY);
  return days < 0 ? null : days;
}

function daysPill(days: number | null): { label: string; color: string } {
  if (days === null) {
    return { label: 'Toimenpide', color: COLORS.impact.high };
  }
  if (days === 0) return { label: 'Tänään', color: COLORS.impact.critical };
  const color =
    days <= 30 ? COLORS.impact.critical : days <= 90 ? COLORS.impact.high : days <= 180 ? COLORS.impact.medium : COLORS.impact.low;
  return { label: `${days} pv`, color };
}

function stripRetirementPrefix(title: string): string {
  return title
    .replace(/^\[[^\]]*\]\s*/, '')
    .replace(/^(retirement)\s*[:–-]\s*/i, '')
    .trim();
}

function stripTypePrefix(title: string): string {
  return title
    .replace(/^\[[^\]]*\]\s*/, '')
    .replace(
      /^(launched|generally available|rolling out|in development|in preview|update|announcement)\s*[:–-]\s*/i,
      '',
    )
    .trim();
}
